import { useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import { useIsNativeApp } from "@/hooks/useIsNativeApp";

interface TypingMarkdownProps {
  content: string;
  isStreaming: boolean;
  speed?: number;
}

function extractVimeoEmbeds(content: string): { cleanContent: string; videoIds: string[] } {
  const vimeoRegex = /(?:https?:\/\/)?(?:player\.)?vimeo\.com\/(?:video\/)?(\d+)(?:\?[^\s)]*)?/g;
  const videoIds: string[] = [];
  
  let match;
  while ((match = vimeoRegex.exec(content)) !== null) {
    if (!videoIds.includes(match[1])) {
      videoIds.push(match[1]);
    }
  }
  
  const cleanContent = content
    .replace(/^\s*(?:https?:\/\/)?(?:player\.)?vimeo\.com\/(?:video\/)?\d+(?:\?[^\s)]*)?$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  
  return { cleanContent, videoIds };
}

export function TypingMarkdown({ content, isStreaming, speed = 3 }: TypingMarkdownProps) {
  const isNativeApp = useIsNativeApp();
  const [displayedLength, setDisplayedLength] = useState(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isStreaming) {
      setDisplayedLength(content.length);
      return;
    }

    const animate = () => {
      setDisplayedLength(prev => {
        if (prev >= content.length) return prev;
        return Math.min(prev + speed, content.length);
      });
      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [content, isStreaming, speed]);

  const displayedContent = isStreaming
    ? content.slice(0, displayedLength)
    : content;

  const showCursor = isStreaming && displayedLength < content.length;

  const { cleanContent, videoIds } = extractVimeoEmbeds(displayedContent);

  return (
    <div className="prose prose-sm max-w-none dark:prose-invert">
      <ReactMarkdown
        components={{
          h1: ({ node, ...props }) => <h1 className="text-xl font-bold mb-2 mt-3" {...props} />,
          h2: ({ node, ...props }) => <h2 className="text-lg font-bold mb-2 mt-2" {...props} />,
          h3: ({ node, ...props }) => <h3 className="text-base font-semibold mb-1.5 mt-2" {...props} />,
          p: ({ node, ...props }) => <p className="mb-2 leading-relaxed" {...props} />,
          ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-2 space-y-1" {...props} />,
          ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-2 space-y-1" {...props} />,
          li: ({ node, ...props }) => <li className="mb-0.5" {...props} />,
          hr: ({ node, ...props }) => <hr className="my-3 border-t border-border" {...props} />,
          strong: ({ node, ...props }) => <strong className="font-bold" {...props} />,
          em: ({ node, ...props }) => <em className="italic" {...props} />,
          a: ({ node, href, ...props }) => {
            // Im Web: neuer Tab, damit Chat-Verlauf erhalten bleibt.
            // In der nativen App: gleicher WebView, sonst kein Login/keine Inhalte
            // — der Chat wird beim Zurück via sessionStorage wiederhergestellt.
            const isExternal = !!href && /^https?:\/\//i.test(href) && !href.includes(window.location.host);
            const openInNewTab = !isNativeApp || isExternal;
            return (
              <a
                {...props}
                href={href}
                target={openInNewTab ? "_blank" : undefined}
                rel={openInNewTab ? "noopener noreferrer" : undefined}
                className="text-primary hover:underline"
              />
            );
          }
        }}
      >
        {cleanContent}
      </ReactMarkdown>
      {showCursor && (
        <span className="inline-block w-1.5 h-4 ml-0.5 bg-foreground/70 animate-pulse rounded-sm align-text-bottom" />
      )}
      {videoIds.length > 0 && !isStreaming && videoIds.map(id => (
        <div key={id} className="mt-3 rounded-xl overflow-hidden shadow-md">
          <div className="relative w-full" style={{ aspectRatio: '16/9' }}>
            <iframe
              src={`https://player.vimeo.com/video/${id}?title=0&byline=0&portrait=0&speed=1`}
              className="absolute inset-0 w-full h-full"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      ))}
    </div>
  );
}
